import CustomBlock from '@/components/common/custom-block/custom-block';
import useGetAvatars from '@/hooks/useGetAvatars';
import {
    INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
    PLANNED_DATE_AVATAR_PROPERTY,
    STATUS_NAME_VALUES,
} from '@/utils/CONST';
import { Avatar } from '@/utils/types';
import Box from '@mui/material/Box';
import { LoadIndicator } from 'devextreme-react';
import Scheduler, { Resource, View } from 'devextreme-react/scheduler';
import { filter, find, map } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import '@/components/agenda/agenda-scheduler-overrides.css';
import {
    collectClientIdsFromSites,
    collectPlaceIdsFromInterventions,
    collectSiteIdsFromPlaces,
    mergeAgendaEntities,
} from '@/components/agenda/build-agenda-hierarchy';
import AgendaItemCustom from '@/components/agenda/custom/agenda-item-custom';
import AgendaItem from '@/components/agenda/custom/agendaItem';
import AgendaMonthAppointmentItem from '@/components/agenda/custom/agenda-month-appointment-item';
import { AppointmentType } from '@/components/agenda/types';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import 'devextreme/dist/css/dx.light.css';

const EMPTY_AVATARS: Avatar[] = [];

/** Tipos de intervención = recurso del Scheduler (catálogo compartido; doc: varias citas con el mismo resource id). */
const INTERVENTION_TYPE_RESOURCE_SPECS = [
    { interventionTypeId: 'preventive', color: '#ff9747' },
    { interventionTypeId: 'under_quote', color: '#1e90ff' },
    { interventionTypeId: 'audit_out_of_contract', color: '#cb6bb2' },
] as const;

/** Solo día `YYYY-MM-DD`: DevExtreme trata varias citas como el mismo instante y las superpone en agenda. */
const DATE_ONLY_PLANNED = /^\d{4}-\d{2}-\d{2}$/;

/** Fecha calendario + franja en UTC para que `startDate`/`endDate` no colapsen en el mismo instante. */
function dateOnlyDayToSlotRangeUtc(dayKey: string, slotIndex: number, slotMinutes: number) {
    const y = parseInt(dayKey.slice(0, 4), 10);
    const m = parseInt(dayKey.slice(5, 7), 10) - 1;
    const d = parseInt(dayKey.slice(8, 10), 10);
    const minutesFromMidnightUtc = 12 * 60 + slotIndex * slotMinutes;
    const h = Math.floor(minutesFromMidnightUtc / 60);
    const min = minutesFromMidnightUtc % 60;
    const start = new Date(Date.UTC(y, m, d, h, min, 0, 0));
    const end = new Date(start.getTime() + slotMinutes * 60 * 1000);
    return { start, end };
}

function normalizeAgendaDatesForScheduler(items: AppointmentType[]): AppointmentType[] {
    const nextSlotByDay = new Map<string, number>();
    const SLOT_MINUTES = 30;

    return items.map((item) => {
        const startRaw = item.startDate;
        const startStr = typeof startRaw === 'string' ? startRaw.trim() : '';

        if (!DATE_ONLY_PLANNED.test(startStr)) {
            return {
                ...item,
                startDate: moment(startRaw).toDate(),
                endDate: moment(item.endDate).toDate(),
            };
        }

        const dayKey = startStr;
        const slot = nextSlotByDay.get(dayKey) ?? 0;
        nextSlotByDay.set(dayKey, slot + 1);

        const { start, end } = dateOnlyDayToSlotRangeUtc(dayKey, slot, SLOT_MINUTES);

        return {
            ...item,
            startDate: start,
            endDate: end,
        };
    });
}

function sortAppointmentsForScheduler(items: AppointmentType[]): AppointmentType[] {
    return [...items].sort((a, b) => {
        const ta = new Date(a.startDate).getTime();
        const tb = new Date(b.startDate).getTime();
        if (ta !== tb) {
            return ta - tb;
        }
        return String(a.id).localeCompare(String(b.id));
    });
}

const AgendaMain = () => {
    const translate = useTranslate();
    const [data, setData] = useState<AppointmentType[]>([]);
    const [currentView, setCurrentView] = useState<string>('Agenda');
    const lastSchedulerViewRef = useRef(currentView);
    /** Incrementa en cada cambio real de vista: `key={currentView}` repetía "Mois" y React + plantillas asíncronas de devextreme-react dejaban los portales de citas sin contenido al volver al mes. */
    const [schedulerMountGeneration, setSchedulerMountGeneration] = useState(0);
    const [currentDate, setCurrentDate] = useState<Date>(moment().toDate());
    const [loadingData, setLoadingData] = useState(false);
    const [dialogAppointment, setDialogAppointment] = useState<AppointmentType | null>(null);

    const handleAppointmentClick = useCallback(
        (e: any) => {
            e.cancel = true;
            const raw = e.appointmentData as AppointmentType | undefined;
            if (!raw?.id) {
                return;
            }
            const resolved = data.find((d) => d.id === raw.id) ?? raw;
            setDialogAppointment(resolved);
        },
        [data],
    );

    const handlePropertyChange = useCallback((e: any) => {
        //TODO fix the way the month its calculated, since its counting from the current day always.
        if (e.name === 'currentDate') {
            setCurrentDate(moment(e.value).toDate());
        }
        if (e.name === 'currentView') {
            const nextView = e.value as string;
            if (nextView !== lastSchedulerViewRef.current) {
                lastSchedulerViewRef.current = nextView;
                setSchedulerMountGeneration((n) => n + 1);
                setCurrentView(nextView);
            }
        }
    }, []);

    // --- 1. Intervenciones: listado en rango de fechas; `read_avatar_parent` da la ref al lugar ---
    const { avatars: interventions, isLoading: isLoadingInterventions } = useGetAvatars({
        filters: {
            'currentNode.name': STATUS_NAME_VALUES[1],
            serialization_groups: ['read_avatar_parent'],
            'properties.name': PLANNED_DATE_AVATAR_PROPERTY,
            'properties.value[after]': moment(currentDate).format('YYYY-MM-DD HH:mm:ssZ'),
            'properties.value[before]': moment(currentDate)
                .add(1, currentView === 'Mois' ? 'month' : 'week')
                .format('YYYY-MM-DD HH:mm:ssZ'),
        },
    });

    const interventionList = interventions != null ? interventions : EMPTY_AVATARS;
    const placeIds = collectPlaceIdsFromInterventions(interventionList);

    // --- 2. Lugares (places): request por ids de parents de intervenciones; trae ref al sitio ---
    const { avatars: places, isLoading: isLoadingPlaces } = useGetAvatars({
        filters: {
            id: placeIds,
            serialization_groups: ['read_avatar_root', 'read_avatar_parent'],
        },
        enabled: placeIds.length > 0 && !isLoadingInterventions,
    });

    const placeList = places != null ? places : EMPTY_AVATARS;
    const siteIds = collectSiteIdsFromPlaces(placeList);

    // --- 3. Sitios (sites): request por ids de parents de places; trae ref al cliente ---
    const { avatars: sites, isLoading: isLoadingSites } = useGetAvatars({
        filters: {
            id: siteIds,
            serialization_groups: ['read_avatar_root', 'read_avatar_parent'],
        },
        enabled: siteIds.length > 0 && !isLoadingPlaces && !isLoadingInterventions,
    });

    const siteList = sites != null ? sites : EMPTY_AVATARS;
    const clientIds = collectClientIdsFromSites(siteList);

    // --- 4. Clientes (clients): request por ids de parents de sites ---
    const { avatars: clients, isLoading: isLoadingClients } = useGetAvatars({
        filters: {
            id: clientIds,
            serialization_groups: ['read_avatar_root'],
        },
        enabled: clientIds.length > 0 && !isLoadingSites && !isLoadingPlaces && !isLoadingInterventions,
    });

    const interventionTypeResourceDataSource = useMemo(
        () =>
            INTERVENTION_TYPE_RESOURCE_SPECS.map(({ interventionTypeId, color }) => ({
                id: interventionTypeId,
                text: translate(`intervention.list.${interventionTypeId}`),
                color,
            })),
        [translate],
    );

    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties({
        avatarId: map(interventionList, 'id'),
        enabled: !isLoadingInterventions && interventionList.length > 0,
    });

    const transformAvatarIntoAgenda = (avatars: Avatar[], hierarchy: ReturnType<typeof mergeAgendaEntities>) => {
        let finalData: AppointmentType;

        return avatars?.map((item) => {
            const planned_date: any = find(
                properties,
                ({ name, avatar }) => name === PLANNED_DATE_AVATAR_PROPERTY && avatar === item.id,
            )?.value;

            const interventionTypeId: any = find(
                properties,
                ({ name, avatar }) => name === INTERVENTION_CREATED_FROM_AVATAR_PROPERTY && avatar === item.id,
            )?.value;

            const chain = hierarchy.byInterventionId[item.id];
            const place = chain?.placeId ? hierarchy.placesById.get(chain.placeId) : undefined;
            const site = chain?.siteId ? hierarchy.sitesById.get(chain.siteId) : undefined;

            const siteName = site?.name;
            const clientName =
                (chain?.clientId ? hierarchy.clientsById.get(chain.clientId)?.name : undefined) ??
                place?.customFields?.['parent.name'];
            const placeName = place?.name ?? (item.parent as Avatar | undefined)?.name;

            const endDate = planned_date;
            const startDate = planned_date;

            const calendarLabel = [clientName, siteName, placeName]
                .map((n) => (n && String(n).trim()) || '--')
                .join(' | ');

            const trimField = (v: unknown) => (v != null && String(v).trim() ? String(v).trim() : '');
            const postal = trimField(site?.postalCode) || trimField(place?.postalCode);
            const city = trimField(site?.city) || trimField(place?.city);
            const postalCityLine = [postal, city].filter(Boolean).join(', ');

            const typeSpec = INTERVENTION_TYPE_RESOURCE_SPECS.find((s) => s.interventionTypeId === interventionTypeId);

            finalData = {
                id: item.id,
                avatarId: item.id,
                interventionTypeId,
                endDate,
                startDate,
                text: calendarLabel,
                postalCityLine,
                color: typeSpec?.color,
                parentSiteName: siteName,
                parentClientName: clientName,
                parentPlaceName: placeName,
                placeId: chain?.placeId ?? undefined,
                siteId: chain?.siteId ?? undefined,
                clientId: chain?.clientId ?? undefined,
            };

            return finalData;
        });
    };

    const isLoadingAgendaGraph =
        isLoadingInterventions ||
        (placeIds.length > 0 && isLoadingPlaces) ||
        (siteIds.length > 0 && isLoadingSites) ||
        (clientIds.length > 0 && isLoadingClients);

    useEffect(() => {
        setLoadingData(true);
        if (interventions == null || properties == null || isLoadingAgendaGraph) {
            setLoadingData(false);
            return;
        }
        const intList = interventions;
        const pl = places != null ? places : EMPTY_AVATARS;
        const sl = sites != null ? sites : EMPTY_AVATARS;
        const cl = clients != null ? clients : EMPTY_AVATARS;
        const hierarchy = mergeAgendaEntities(intList, pl, sl, cl);
        const response = transformAvatarIntoAgenda(intList, hierarchy);
        const filtered_values = filter(response, (item) => !!item?.endDate && !!item?.startDate);
        const normalized = normalizeAgendaDatesForScheduler(filtered_values);
        const forScheduler = sortAppointmentsForScheduler(normalized);

        setData(forScheduler);
        setLoadingData(false);
    }, [interventions, properties, places, sites, clients, isLoadingAgendaGraph]);

    return (
        <CustomBlock label={translate('main.navigation.agenda')}>
            {!loadingData && !isLoadingProperties && !isLoadingAgendaGraph && (
                <Box
                    className='agenda-scheduler-scope'
                    sx={{
                        width: '100%',
                        minHeight: 0,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        '& > .dx-scheduler': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
                    }}
                >
                    <Scheduler
                        key={`${currentView}-${schedulerMountGeneration}`}
                        dataSource={data}
                        currentView={currentView as any}
                        currentDate={currentDate}
                        editing={false}
                        onOptionChanged={handlePropertyChange}
                        onAppointmentClick={handleAppointmentClick}
                        onAppointmentDblClick={(e) => {
                            e.cancel = true;
                        }}
                        onAppointmentFormOpening={(e) => {
                            e.cancel = true;
                        }}
                    >
                        <View
                            name='Agenda'
                            type='agenda'
                            appointmentComponent={AgendaItemCustom}
                        />
                        <View
                            name='Mois'
                            type='month'
                            appointmentComponent={AgendaMonthAppointmentItem}
                        />

                        <Resource
                            label={translate('intervention.list.type')}
                            dataSource={interventionTypeResourceDataSource}
                            fieldExpr='interventionTypeId'
                        />
                    </Scheduler>

                    {dialogAppointment != null && (
                        <AgendaItem
                            appointmentData={dialogAppointment}
                            open
                            onClose={() => setDialogAppointment(null)}
                        />
                    )}
                </Box>
            )}

            {isLoadingAgendaGraph && (
                <Box
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        bgcolor: 'rgb(0,0,0, 0.1)',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <LoadIndicator />
                </Box>
            )}
        </CustomBlock>
    );
};

export default AgendaMain;
